import { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import { getSourceSystem, getThirdPartySupported } from './utils';
import {
  EHR_MYCHART,
  EHR_MYCHART_EMBEDDED,
  EHR_CERNER_SMART,
  FHIR_PID_TYPE
} from './constants';
import { useCallback } from 'react';
import { fetchEpicTokenResponse } from './api/mychart';
import { fetchSessionInfo } from './api/mychart_embedded';
import { fetchSessionInfo as fetchSmartSessionInfo } from './api/smart';
import { THIRD_PARTY } from '@kyruus/direct-book-widget';

export const useEhrData = ({ config }) => {
  const [data, setData] = useState(null);
  const [errors, setErrors] = useState([]);
  // Used to force a re-render when the EHR data is cleared out
  const [clearCount, setClearCount] = useState(0);

  useEffect(() => {
    const { type, loginConfig } = getThirdPartySupported(config);
    if (type === EHR_MYCHART) {
      /* client side oauth authentication.
       * TODO: migrate to server-side approach similar to the mychart_embedded login flow
       */
      const oauthCode = Cookies.get('oauthCode');
      const { issuerToSourceSystem } = loginConfig;
      if (oauthCode) {
        (async () => {
          try {
            const { patient: pid = '', access_token: accessToken = '' } =
              await fetchEpicTokenResponse({
                oauthCode
              });

            const sourceSystem = getSourceSystem(
              accessToken,
              issuerToSourceSystem,
              type
            );

            setData({
              pid,
              pid_type: FHIR_PID_TYPE,
              source_system: sourceSystem
            });

            if (!pid) {
              setErrors((prevErrors) => {
                return [...prevErrors, THIRD_PARTY.ERROR.ERROR_EHR_INVALID_PID];
              });
            }
            if (!accessToken) {
              setErrors((prevErrors) => {
                return [
                  ...prevErrors,
                  THIRD_PARTY.ERROR.ERROR_EHR_INVALID_ACCESS_TOKEN
                ];
              });
            }
            if (!sourceSystem) {
              setErrors((prevErrors) => {
                return [
                  ...prevErrors,
                  THIRD_PARTY.ERROR.ERROR_EHR_INVALID_SOURCE_SYSTEM
                ];
              });
            }
            Cookies.remove('oauthCode');
          } catch (error) {
            Cookies.remove('oauthCode');
            setData({});
            setErrors((prevErrors) => {
              return [...prevErrors, THIRD_PARTY.ERROR.ERROR_EHR_LOGIN];
            });
          }
        })();
      } else {
        setData({});
      }
      // mychart embedded does a SMART handshake at the beginning of the session and stores info in the server session
      // which we retrieve with an api call
    } else if (type === EHR_MYCHART_EMBEDDED) {
      (async () => {
        try {
          const embeddedConfig = config?.third_party?.mychart_embedded;
          const sessionInfo = await fetchSessionInfo();
          const { patientId, accessToken } = sessionInfo;
          const sourceSystem = getSourceSystem(
            accessToken,
            embeddedConfig.issuer_to_source_system,
            type
          );
          setData({
            pid: patientId,
            pid_type: FHIR_PID_TYPE,
            source_system: sourceSystem
          });
        } catch (e) {
          setData({});
          setErrors((prevErrors) => {
            return [...prevErrors, THIRD_PARTY.ERROR.ERROR_EHR_LOGIN];
          });
        }
      })();
    } else if (type === EHR_CERNER_SMART) {
      (async () => {
        try {
          const sessionInfo = await fetchSmartSessionInfo();
          const { patientId, accessToken } = sessionInfo;
          const cernerConfig = config?.third_party?.cerner;
          const sourceSystem = getSourceSystem(
            accessToken,
            cernerConfig?.issuer_to_source_system,
            type
          );
          setData({
            pid: patientId,
            pid_type: FHIR_PID_TYPE,
            source_system: sourceSystem
          });
        } catch (e) {
          setData({});
          setErrors((prevErrors) => {
            return [...prevErrors, THIRD_PARTY.ERROR.ERROR_EHR_LOGIN];
          });
        }
      })();
    } else {
      setData({});
    }
    // compare a string representation of config as sometimes the reference changes
    // but not the contents. We don't want to re-render if config hasn't changed because
    // this hook is destructive (removes cookies)
  }, [JSON.stringify(config), clearCount]);

  /**
   * Function that clears out EHR data. Useful for logout functionality.
   */
  const clearData = useCallback(() => {
    setData(null);
    setClearCount((prev) => prev + 1);
  }, [setData, setClearCount]);

  return { data, errors, clearData };
};
